import { ChangeDetectorRef, Component, Inject, ViewChild } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { MatCalendar, MatCalendarCellClassFunction, MatCalendarCellCssClasses } from '@angular/material/datepicker';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import moment from 'moment';
import { PopupAlertComponent } from '../popup-alert/popup-alert.component';
import { DialogService } from 'src/app/services/dialog.service';
import { MatMenuTrigger } from '@angular/material/menu';

@Component({
  selector: 'app-schedule-fromdate-todate',
  templateUrl: './schedule-fromdate-todate.component.html',
  styleUrls: ['./schedule-fromdate-todate.component.scss']
})
export class ScheduleFromDateToDateDialogComponent {
  fromTimeSlots: any[] = [];
  toTimeSlots: any[] = [];
  today: moment.Moment = moment().startOf('day'); 
  scheduleForm!: FormGroup;

  @ViewChild('fromCalendar') fromCalendar!: MatCalendar<Date>;
  @ViewChild('toCalendar') toCalendar!: MatCalendar<Date>;
  @ViewChild('fromTimeMenuTrigger') fromTimeMenuTrigger!: MatMenuTrigger;
  @ViewChild('toTimeMenuTrigger') toTimeMenuTrigger!: MatMenuTrigger;

  constructor(public dialogRef: MatDialogRef<ScheduleFromDateToDateDialogComponent>,
    @Inject(MAT_DIALOG_DATA) public data: any,
    private fb: FormBuilder,
    private cdr: ChangeDetectorRef,
    private dialogService: DialogService
  ) {
  }

  ngOnInit(): void {
    this.scheduleForm = new FormGroup({
      fromDate: this.fb.control(null, Validators.required),
      fromTime: this.fb.control('', Validators.required),
      toDate: this.fb.control(null, Validators.required),
      toTime: this.fb.control('', Validators.required),
    });

    this.generateFromTimeSlots();
    this.generateToTimeSlots();

    if (this.data?.startDateTime && this.data.endDateTime) {
      this.patchScheduleData(this.data.startDateTime, this.data.endDateTime);
    }
  }

  patchScheduleData(startDate: string, toData: string): void {
    const extractDateTime = (dateTime: string) => {
      const parts = dateTime.split(" | ");
      return {
        date: parts[0] ? moment(parts[0], "MM-DD-YYYY").toDate() : null,
        time: parts[1] ? parts[1].trim() : ""
      };
    };

    const startDateTime = extractDateTime(startDate);
    const toDateTime = extractDateTime(toData);

    if (startDateTime && toDateTime) {
      this.scheduleForm.patchValue({
        fromDate: startDateTime.date,
        fromTime: this.fromTimeSlots.length > 0 ? startDateTime.time : '',
        toDate: toDateTime.date,
        toTime: '',
      });

      this.generateToTimeSlots();

      if (this.toTimeSlots.length > 0) {
        this.scheduleForm.get('toTime')?.patchValue(toDateTime.time);
      }

      this.cdr.detectChanges();
    }
  }

  generateFromTimeSlots(): void {
    const now = new Date();
    const start = new Date();
    start.setHours(0, 0, 0, 0);
    const end = new Date();
    end.setHours(23, 59, 59, 999);

    const interval = 15; // 15-minute interval
    const times = [];
    let current = start;

    while (current <= end) {
      const hours = current.getHours();
      const minutes = current.getMinutes();
      const period = hours >= 12 ? 'PM' : 'AM';
      const formattedHours = hours % 12 || 12; // Convert to 12-hour format
      const formattedMinutes = minutes.toString().padStart(2, '0');
      const timeLabel = `${formattedHours}:${formattedMinutes} ${period}`;
      // Include AM/PM in timeValue for the 12-hour format
      const timeValue = `${formattedHours.toString().padStart(2, '0')}:${formattedMinutes} ${period}`;
      const selectedDt = moment(this.scheduleForm.get('fromDate')?.value, 'MM/DD/YYYY');
      const isPast = selectedDt.isSame(moment(now), 'day') && current < now;
      times.push({ label: timeLabel, value: timeValue, disabled: isPast });
      current.setMinutes(current.getMinutes() + interval);
    }

    this.fromTimeSlots = times;
  }

  generateToTimeSlots(): void {
    if (!this.scheduleForm.get('toDate')?.value || !this.scheduleForm.get('fromTime')?.value) {
      return;
    }

    const now = new Date();
    const start = new Date();
    start.setHours(0, 0, 0, 0);
    const end = new Date();
    end.setHours(23, 59, 59, 999);

    const interval = 15; // 15-minute interval
    const times = [];
    let current = start;

    const selectedFromMoment = moment(this.scheduleForm.get('fromTime')?.value, 'hh:mm A');

    while (current <= end) {
      const hours = current.getHours();
      const minutes = current.getMinutes();
      const period = hours >= 12 ? 'PM' : 'AM';
      const formattedHours = hours % 12 || 12;
      const formattedMinutes = minutes.toString().padStart(2, '0');
      const timeLabel = `${formattedHours}:${formattedMinutes} ${period}`;
      const timeValue = `${formattedHours.toString().padStart(2, '0')}:${formattedMinutes} ${period}`;

      const selectedToMoment = moment(timeValue, 'hh:mm A');
      let fromDate = this.scheduleForm.get('fromDate')?.value
      let toDate = this.scheduleForm.get('toDate')?.value
      const isSameDate = fromDate && toDate && moment(fromDate).isSame(toDate, 'day');
      const isDisabled = (isSameDate && selectedToMoment.isBefore(selectedFromMoment)) ||
        (moment(toDate).isSame(moment(now), 'day') && current < now);

      times.push({ label: timeLabel, value: timeValue, disabled: isDisabled });
      current.setMinutes(current.getMinutes() + interval);
    }

    this.toTimeSlots = times;
    this.cdr.detectChanges(); // ✅ Ensure UI updates
  }


  onFromTimeChange(): void {
    if (this.scheduleForm.get('fromDate')?.value && this.scheduleForm.get('toDate')?.value && moment(this.scheduleForm.get('fromDate')?.value).isSame(this.scheduleForm.get('toDate')?.value, 'day')) {
      this.scheduleForm.get('toTime')?.setValue('');
      this.toTimeSlots = [];
      this.generateToTimeSlots();
    }

    this.cdr.detectChanges();
  }

  selectFromDate(event: any): void {
   // if (!event) return;
      // console.log(event);
      
      // const date = moment(event).startOf('day');
      // if (this.isDisabled(date.toDate())) return;

      // this.scheduleForm.get('fromDate')?.setValue(date.toDate());

      // // ✅ Only reset toDate if it's before fromDate
      // if (this.scheduleForm.get('toDate')?.value && moment(this.scheduleForm.get('toDate')?.value).isBefore(this.scheduleForm.get('fromDate')?.value, 'day')) {
      //   this.scheduleForm.get('toDate')?.setValue(null);
      // }

      // this.scheduleForm.get("fromTime")?.setValue('');
      // this.scheduleForm.get('toTime')?.setValue('');
      // this.generateFromTimeSlots();
    this.generateToTimeSlots();
    this.cdr.detectChanges();

    this.fromTimeMenuTrigger.openMenu();
  }



  selectToDate(event: any): void {
    // if (!event) return;
    // const date = moment(event).startOf('day');
    // if (this.isDisabled(date.toDate())) return;
    // this.scheduleForm.get('toDate')?.setValue(date.toDate());
    // this.scheduleForm.get('toTime')?.setValue('');
    this.generateToTimeSlots();
    this.toTimeMenuTrigger.openMenu();
    this.cdr.detectChanges();
  }

  isFromDateSelected: MatCalendarCellClassFunction<Date> = (date: Date): MatCalendarCellCssClasses => {
    if (!this.scheduleForm.get('fromDate')?.value) return '';

    const isSelected = this.scheduleForm.get('fromDate')?.value && moment(date).isSame(this.scheduleForm.get('fromDate')?.value, 'day');
    return isSelected ? 'fromDateSelected' : '';
  };

  isToDateSelected: MatCalendarCellClassFunction<Date> = (date: Date): MatCalendarCellCssClasses => {
    if (!this.scheduleForm.get('toDate')?.value) return '';

    const isSelected = this.scheduleForm.get('toDate')?.value && moment(date).isSame(this.scheduleForm.get('toDate')?.value, 'day');
    return isSelected ? 'toDateSelected' : '';
  };

  isDisabled = (date: Date): boolean => {
    const momentDate = moment(date).startOf('day');
    if (momentDate.isBefore(this.today)) {
      return true;
    }
    return false;
  };

  // dateFilterFrom = (date: Date | null): boolean => {
  //   if (!date) return false;
  //   const momentDate = moment(date).startOf('day');
  //   return !momentDate.isBefore(this.today);
  // };

  dateFilterFrom = (date: Date | null): boolean => {
    if (!date) return false;
    const momentDate = moment(date).startOf('day');
    return !momentDate.isBefore(this.today); // Only allow dates that are today or in the future
  };



  dateFilterTo = (date: Date | null): boolean => {
    if (!date) return false;
    const momentDate = moment(date).startOf('day');

    if (momentDate.isBefore(this.today)) return false;
    if (this.scheduleForm.get('fromDate')?.value && momentDate.isBefore(moment(this.scheduleForm.get('fromDate')?.value), 'day')) return false;

    return true;
  };

  setCurrentTime() {
    const now = new Date();
    this.scheduleForm.get('fromDate')?.setValue(new Date(now.toISOString().split('T')[0]));
    let minutes = now.getMinutes();
    let roundedMinutes = Math.round(minutes / 15) * 15;

    if (roundedMinutes === 60) {
      now.setHours(now.getHours() + 1);
      roundedMinutes = 0;
    }

    let hours = now.getHours();
    const period = hours >= 12 ? 'PM' : 'AM';
    const formattedHours = (hours % 12 || 12).toString().padStart(2, '0');
    const formattedTime = `${formattedHours}:${roundedMinutes.toString().padStart(2, '0')} ${period}`;

    setTimeout(() => {
      this.scheduleForm.get('fromTime')?.setValue(formattedTime);
      this.cdr.detectChanges();
    });
  }

  applySchedule() {
    console.log(this.scheduleForm.value);
    if (this.scheduleForm.invalid) {
      this.dialogService.openDialog(PopupAlertComponent, 'Please fill all the mandatory fields...!');
      return;
    } else {


      this.dialogRef.close(this.scheduleForm.getRawValue());
    }

  }

  get fromDateTimeDisplay(): string {
    const date = this.scheduleForm.get('fromDate')?.value;
    const time = this.scheduleForm.get('fromTime')?.value;
  
    // const formattedDate = date
    //   ? new Intl.DateTimeFormat('en-GB', { day: '2-digit', month: '2-digit', year: 'numeric' }).format(new Date(date))
    //   : '';
    const formattedDate = date ? moment(date).format('MM-DD-YYYY') : '';
  
    return (formattedDate && time) ? `${formattedDate} ${time}` : formattedDate || time || '';
  }  
  

  onTimeOptionSelected(time: any): void {
    console.log("Time option selected:", time);
    
    this.scheduleForm.patchValue({
      fromTime: time.value
    }); // Important!\
    console.log(this.scheduleForm.value);
  }
  
  get toDateTimeDisplay(): string {
    const date = this.scheduleForm.get('toDate')?.value;
    const time = this.scheduleForm.get('toTime')?.value;
  
    const formattedDate = date ? moment(date).format('MM-DD-YYYY') : '';
  
    return (formattedDate && time) ? `${formattedDate} ${time}` : formattedDate || time || '';
  }

  onToTimeSelected(time: { label: string; value: string }) {
    this.scheduleForm.patchValue({ toTime: time.value });
  }


}
